class HypematCartUpsell extends HTMLElement {
  constructor() {
    super();
    this._onClick = this._onClick.bind(this);
  }

  connectedCallback() {
    this.addEventListener("click", this._onClick);
  }

  disconnectedCallback() {
    this.removeEventListener("click", this._onClick);
  }

  async _onClick(event) {
    const button = event.target.closest("[data-upsell-add]");

    if (!button || button.disabled) {
      return;
    }

    event.preventDefault();

    const variantId = button.getAttribute("data-variant-id");
    const productTitle = button.getAttribute("data-product-title") || "";

    if (!variantId) {
      return;
    }

    this._setButtonLoading(button, true);

    try {
      const response = await fetch(this._cartAddUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections: ["mini-cart"]
        })
      });

      const responseJson = await response.json();

      if (!response.ok) {
        throw new Error(responseJson.description || "Cart upsell add failed");
      }

      this._setButtonSuccess(button, productTitle);

      const cartResponse = await fetch(this._cartJsonUrl);
      const cart = await cartResponse.json();

      if (responseJson.sections) {
        cart.sections = responseJson.sections;
      }

      document.documentElement.dispatchEvent(new CustomEvent("cart:updated", {
        bubbles: true,
        detail: { cart }
      }));

      document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", {
        bubbles: true,
        detail: { cart }
      }));

      this._hideCard(button);
    } catch (error) {
      console.error(error);
      this._setButtonError(button);
    } finally {
      if (button.classList.contains("is-success")) {
        button.classList.remove("is-loading");
        button.removeAttribute("aria-busy");
      } else {
        this._setButtonLoading(button, false);
      }
    }
  }

  _setButtonLoading(button, isLoading) {
    if (isLoading) {
      button.dataset.originalContent = button.textContent;
      button.disabled = true;
      button.setAttribute("aria-busy", "true");
      button.classList.add("is-loading");
      button.textContent = "...";
    } else {
      button.disabled = false;
      button.removeAttribute("aria-busy");
      button.classList.remove("is-loading");
    }
  }

  _setButtonSuccess(button, productTitle) {
    button.classList.remove("is-loading");
    button.classList.add("is-success");
    button.textContent = button.getAttribute("data-success-text") || "Added";
    this._showNotification(button, productTitle);
  }

  _setButtonError(button) {
    button.classList.add("is-error");
    button.textContent = button.getAttribute("data-error-text") || "Error";

    window.setTimeout(() => {
      button.classList.remove("is-error");

      if (button.dataset.originalContent) {
        button.textContent = button.dataset.originalContent;
      }
    }, 1800);
  }

  _showNotification(button, productTitle) {
    const notification = document.createElement("div");
    const content = document.createElement("div");
    const icon = document.createElement("span");
    const message = document.createElement("span");
    const title = document.createElement("strong");
    const sentence = button.getAttribute("data-added-sentence") || "was added to your cart";

    notification.className = "cart-upsell-notification";
    content.className = "cart-upsell-notification__content";
    icon.className = "cart-upsell-notification__icon";
    message.className = "cart-upsell-notification__message";

    icon.setAttribute("aria-hidden", "true");
    icon.textContent = "✓";
    title.textContent = productTitle;
    message.append(title, ` ${sentence}`);
    content.append(icon, message);
    notification.append(content);
    document.body.append(notification);

    window.setTimeout(() => notification.classList.add("is-visible"), 10);
    window.setTimeout(() => {
      notification.classList.remove("is-visible");
      window.setTimeout(() => notification.remove(), 300);
    }, 3000);
  }

  _hideCard(button) {
    const card = button.closest(".hypemat-upsell-card");

    if (!card) {
      return;
    }

    card.classList.add("is-removing");
    window.setTimeout(() => {
      card.hidden = true;
    }, 300);
  }

  get _cartAddUrl() {
    const cartAddUrl = window.themeVariables?.routes?.cartAddUrl;
    const root = window.Shopify?.routes?.root || "/";

    return cartAddUrl ? `${cartAddUrl}.js` : `${root}cart/add.js`;
  }

  get _cartJsonUrl() {
    const cartUrl = window.themeVariables?.routes?.cartUrl;
    const root = window.Shopify?.routes?.root || "/";

    return cartUrl ? `${cartUrl}.js` : `${root}cart.js`;
  }
}

if (!window.customElements.get("hypemat-cart-upsell")) {
  window.customElements.define("hypemat-cart-upsell", HypematCartUpsell);
}
